import React from "react";
import { withTranslation } from "react-i18next";
import { Button, Card, CardBody, Input, Spinner } from "reactstrap";
import { toast } from 'react-toastify';
import { freeSubscription, getApiSettings, insertApiSetting, verifyUrl } from "API/ScannerAPI";

class ScannerApiUrlVerification extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      apiKey: "",
      urlToVerify: "",
      verificationResult: null,
      loading: false
    };
  }

  componentDidMount() {
    this.fetchApiKeys()
  }

  async fetchApiKeys() {
    try {
      this.setState({ loading: true });
      const data = await getApiSettings();
      const keys = Array.isArray(data?.data) ? data.data : [];
      this.setState({ apiKey: keys.length > 0 ? (keys[0].apikey || keys[0].apiKey) : "" });
    } catch (error) {
      console.error("Error fetching API keys:", error);
      toast["error"](error.message);
    } finally {
      this.setState({ loading: false });
    }
  }

  // the user may have no API key yet: create one for the console
  async getApiKey() {
    if (this.state.apiKey) {
      return this.state.apiKey;
    }
    const result = await insertApiSetting("console");
    const apiKey = result?.data?.apikey || result?.data?.apiKey;
    if (!apiKey) {
      throw new Error(result?.message || "Unable to create an API key");
    }
    this.setState({ apiKey: apiKey });
    return apiKey;
  }

  // a user without payments gets the free subscription (as on the Payments
  // page) the first time the API refuses for lack of credits
  async verifyWithFreeCredits(url, apiKey) {
    try {
      return await verifyUrl(url, apiKey);
    } catch (error) {
      if (!error || !error.message || !error.message.includes("credit limit")) {
        throw error;
      }
      await freeSubscription();
      return await verifyUrl(url, apiKey);
    }
  }

  handleVerifyUrl = () => {
    if (!this.state.urlToVerify.trim()) {
      return;
    }
    this.setState({ 
      verificationResult: null, 
      loading: true 
    });
    this.getApiKey()
      .then(apiKey => this.verifyWithFreeCredits(this.state.urlToVerify.trim(), apiKey))
      .then(result => {
        const url = Array.isArray(result?.urls) && result.urls.length > 0 ? result.urls[0] : {};
        const analysis = Array.isArray(url?.data?.analysis) && url.data.analysis.length > 0 ? url.data.analysis[0] : {};
        const response = {
          url: analysis?.final_url || url?.url || '',
          domain: analysis?.domain || url?.domain || '',
          classification: url?.classification || 'UNKNOWN',
          status: url?.classification === 'LEGITIMATE' ? 'valid' : 'invalid',
          reason: url?.classification === 'LEGITIMATE' ? 'Safe domain' : 'Rejected domain',
          ipAddress: Array.isArray(analysis?.ipInfo) && analysis.ipInfo.length > 0 ? analysis.ipInfo[0]?.ip || '?' : '?',
          isMalicious: analysis?.malicious_extension ?? '?'
        };
        this.setState({ verificationResult: response });
      }).catch(error => {
        console.error(error);
        toast["error"](error.message);
      }).finally(() => {
        this.setState({ loading: false });
      });
    };

  render() {
    const { t } = this.props;
    const { verificationResult } = this.state;
    const isResultValid = verificationResult && verificationResult.status === "valid";
    const bannerColor = isResultValid ? "#28a745" : "#dc3545";
    const bannerText = isResultValid
      ? t("phish_guard_url_checker.url_verification.valid_banner")
      : t("phish_guard_url_checker.url_verification.invalid_banner");

    return (
      <div className="scanner-url-verification-container">
        <div className="verification-header">
          <h4>{t("phish_guard_url_checker.url_verification.header")}</h4>
        </div>
        <Card className="mt-3">
          <CardBody>
            <div className="input-row">
              <Input type="text" placeholder={t("phish_guard_url_checker.url_verification.input_placeholder")}
                value={this.state.urlToVerify} onChange={(e) => this.setState({ urlToVerify: e.target.value })}
              />
              <Button color="success" onClick={this.handleVerifyUrl}>
                {t("phish_guard_url_checker.url_verification.verify_button")}
              </Button>
            </div>
          </CardBody>
        </Card>
        {this.state.loading ? (
          <div style={{ textAlign: "center", width: "100%", marginTop: "2rem" }}>
            <Spinner color="primary" />
          </div>
        ) : (this.state.verificationResult && (
          <Card className="mt-3">
            <div className="verification-status-banner" style={{ backgroundColor: bannerColor }}>
              <h5 className="mb-0 text-white">{bannerText}</h5>
            </div>
            <CardBody>
              <h6>{t("phish_guard_url_checker.url_verification.result_title")}</h6>
              <table className="verification-details">
                <tbody>
                  {Object.entries(this.state.verificationResult).map(
                    ([key, value]) => (
                      <tr key={key}>
                        <td className="field-name">{key}</td>
                        <td className="field-value">{String(value)}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </CardBody>
          </Card>)
        )}
      </div>
    );
  }
}

export default withTranslation()(ScannerApiUrlVerification);
